// exclude-from-index
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import { SVGIcon } from '@custom-components';
import withStyles from '@mui/styles/withStyles';
import { withTheme } from 'styled-components';
import { themePropType } from '@themes';
import { flowRight as compose } from 'lodash';
import {
  MobileWrapper,
  MenuHamburger,
  HomeLinkContainer,
  UserButtonContainer,
  ProfilePic,
  AutoAvatar,
  MenuFlyout,
  FlyoutBar,
  CloseBack,
  SpacerContainer,
  ContactIcon,
  FlyoutContainer,
  Underlay,
  UserFlyout,
  StyledSignOut,
  UserPreview,
  PreviewNumbers,
  PreviewNumber,
  UserPic,
  UserAvatar
} from './MobileNav.styles';
import SubMenuExpanded from './SubMenuExpanded';
import LocaleLink from '../../../../components/LocaleLink';
import NavLink from '../common/NavLink';
import OrgSelect from '../common/OrgSelect';
import LinkList from '../common/LinkList';
import Logo from '../common/Logo';
import { colors } from '../../../css';
import { EnvBanner } from '../../../../components/EnvBanner';

export const linkPropType = PropTypes.shape({
  id: PropTypes.string,
  href: PropTypes.string,
  label: PropTypes.string,
  className: PropTypes.string,
  target: PropTypes.string,
  onClick: PropTypes.func,
  icon: PropTypes.string,
  routeMatches: PropTypes.arrayOf(PropTypes.string)
});

export const subLinkPropType = PropTypes.shape({
  id: PropTypes.string,
  href: PropTypes.string,
  label: PropTypes.string,
  className: PropTypes.string,
  target: PropTypes.string,
  onClick: PropTypes.func,
  icon: PropTypes.string,
  routeMatches: PropTypes.arrayOf(PropTypes.string),
  subMenu: PropTypes.arrayOf(linkPropType)
});

class MobileNav extends Component {
  static getCurrentOrgName(userCurrentOrganization, organizations) {
    const currentOrg =
      organizations.length > 0
        ? organizations.find((organization) => organization.id === userCurrentOrganization)
        : undefined;
    const orgName = currentOrg ? currentOrg.name : undefined;

    return orgName;
  }

  static createNavLinks(links, cbFunc) {
    // if triggered again with no links, return null right away.
    if (links === null || links === undefined) {
      return null;
    }
    const extraCallback = typeof cbFunc === 'function' ? cbFunc : () => {};

    // handles if there is an array of links to make.
    if (Array.isArray(links)) {
      const reactLinks = [];

      links.forEach((linkObj) => {
        const interceptedOnClick =
          linkObj.onClick && typeof linkObj.onClick === 'function'
            ? (e) => {
                linkObj.onClick(e, extraCallback);
              }
            : extraCallback;

        reactLinks.push(
          <NavLink
            key={linkObj.id}
            href={linkObj.href || '#'}
            onClick={interceptedOnClick}
            className={linkObj.className || null}
            target={linkObj.target || null}
          >
            {linkObj.label}
          </NavLink>
        );
      });

      return reactLinks;
    }

    // handles single link
    if (!Array.isArray(links) && typeof links === 'object') {
      const interceptedOnClick =
        links.onClick && typeof links.onClick === 'function'
          ? (e) => {
              links.onClick(e, extraCallback);
            }
          : extraCallback;
      return (
        <NavLink
          href={links.href || '#'}
          onClick={interceptedOnClick}
          target={links.target || null}
          className={links.className || null}
        >
          {links.label}
        </NavLink>
      );
    }

    return null;
  }

  constructor(props) {
    super(props);

    this.state = {
      // keeps track of the open menus
      menusOpen: [],
      // passed to let other components know nav is visible or not and to react to that
      menuVisible: false,
      // grabs the current organization name based on id passed and the organizations passed. used in displaying to user.
      currentOrgName: MobileNav.getCurrentOrgName(props.userCurrentOrganization, props.organizations)
    };

    // track y poistion when flyouts open
    this.flyoutOpenY = null;
  }

  componentWillUnmount() {
    /* eslint-disable no-undef */

    // Enable page scrolling when nav is unmounted
    document.body.classList.remove('no-scrolling');

    /* eslint-disable no-undef */
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const { currentOrgName } = prevState;
    const { userCurrentOrganization, organizations } = nextProps;
    const newOrgName = MobileNav.getCurrentOrgName(userCurrentOrganization, organizations);

    const newState = {};

    // force state of the menu being open to close when nav is hidden
    // also call focus on something else to close search flyout
    if (currentOrgName !== newOrgName && newOrgName !== undefined) {
      newState.currentOrgName = newOrgName;
    }

    return newState;
  }

  openMenu = (e, menu) => {
    e.preventDefault();
    const { menusOpen } = this.state;
    // flag to set menuVisible in setstate.
    let flyoutVisible = false;

    // add the opened menu to state
    const newArray = [...menusOpen, menu];

    flyoutVisible = true;

    // track current window scorll position to restore when flyouts close.
    if (this.flyoutOpenY === null) {
      this.flyoutOpenY = window.pageYOffset || document.documentElement.scrollTop;
    }

    this.setState({
      menusOpen: newArray,
      menuVisible: flyoutVisible
    });
  };

  closeMenu = (e) => {
    if (e) {
      e.preventDefault();
    }
    const { menusOpen } = this.state;

    // remove the last item from the array of menu's open.
    const newArray = [...menusOpen];
    newArray.splice(-1, 1);
    const newState = {
      menusOpen: newArray
    };

    // ensure overlay is shown/hidden properly
    newState.menuVisible = newArray.length > 0 || false;

    if (!newState.menuVisible) {
      this.toggleBodyLock(false);
    }

    this.setState(newState);
  };

  // force all flyouts closed
  closeFlyouts = () => {
    this.setState({ menusOpen: [], menuVisible: false });
    this.toggleBodyLock(false);
  };

  // sets the state to lock the body from scrolling
  toggleBodyLock(state) {
    if (state) {
      document.body.classList.add('no-scrolling');
    } else {
      document.body.classList.remove('no-scrolling');
      if (this.flyoutOpenY !== null) {
        // scroll down
        document.documentElement.scrollTop = this.flyoutOpenY;
        document.body.parentNode.scrollTop = this.flyoutOpenY;
        document.body.scrollTop = this.flyoutOpenY;
        this.flyoutOpenY = null;
      }
    }
  }

  // get profile picture badge
  profileBadge(initials) {
    const { userFirstName, userLastName, userProfilePicture } = this.props;

    return (
      <ProfilePic>
        <AutoAvatar src={userProfilePicture} initials={initials} alt={`${userFirstName} ${userLastName}`} />
      </ProfilePic>
    );
  }

  render() {
    const {
      forceScrollCB,
      userFirstName,
      userLastName,
      userProfilePicture,
      contactNumber,
      subMenuLinks,
      signOut,
      joinOrganization,
      userMenuLinks,
      organizations,
      userCurrentOrganization,
      organizationChange,
      routeName,
      theme,
      helpSupport,
      type,
      isAdminToken
    } = this.props;
    const { menusOpen, menuVisible } = this.state;

    let initials = '??';

    if (userFirstName && userLastName && userFirstName.length > 0 && userLastName.length > 0) {
      initials = `${userFirstName.slice(0, 1)}${userLastName.slice(0, 1)}`;
    } else if (userFirstName && !userLastName && userFirstName.length > 0) {
      initials = `${userFirstName.slice(0, 1)}`;
    } else if (userLastName && !userFirstName && userLastName.length > 0) {
      initials = `${userLastName.slice(0, 1)}`;
    }

    const userJob =
      organizations.length > 0 ? organizations.find((orgRole) => orgRole.id === userCurrentOrganization) : undefined;
    const userJobTitle = userJob ? userJob.role : 'Unknown';
    // THINGS THAT WILL BE PULLED LATER FROM API
    const numRequests = 3;
    const numBookings = 12;
    const numProperties = 9;

    return (
      <>
        <MobileWrapper>
          <MenuHamburger>
            <NavLink
              className="menu"
              href="#"
              onClick={(e) => {
                this.openMenu(e, 'mainMenu');
              }}
            >
              <SVGIcon selectedIcon="hamburger" width="20px" height="20px" />
            </NavLink>
          </MenuHamburger>
          <HomeLinkContainer>
            <Logo className="lodgelink-logo" />
          </HomeLinkContainer>
          <EnvBanner />
          <UserButtonContainer>
            <NavLink
              className="menu"
              href="#"
              onClick={(e) => {
                if (!isAdminToken) {
                  this.openMenu(e, 'userMenu');
                }
              }}
            >
              {this.profileBadge(initials)}
            </NavLink>
          </UserButtonContainer>

          {/* Main Menu Flyout */}
          <CSSTransition
            in={menusOpen.indexOf('mainMenu') > -1}
            timeout={400}
            classNames="flyout"
            onEntering={() => {
              forceScrollCB(true);
            }}
            onEntered={() => {
              this.toggleBodyLock(true);
            }}
            onExited={() => {
              forceScrollCB(false);
            }}
          >
            <MenuFlyout>
              <FlyoutBar>
                <CloseBack
                  width="20px"
                  height="20px"
                  className="close"
                  href="#"
                  onClick={(e) => {
                    this.closeMenu(e);
                  }}
                >
                  <SVGIcon
                    selectedIcon="close"
                    width="20px"
                    height="20px"
                    iconColor={theme ? theme.lightText : 'white'}
                  />
                </CloseBack>
                <SpacerContainer>
                  <ContactIcon className="contact-phone" href={`tel:${contactNumber}`}>
                    <SVGIcon
                      selectedIcon="phone"
                      width="20px"
                      height="20px"
                      iconColor={theme ? theme.lightText : 'white'}
                    />
                  </ContactIcon>
                </SpacerContainer>
              </FlyoutBar>
              <FlyoutContainer>
                <SubMenuExpanded
                  subMenuLinks={subMenuLinks}
                  routeName={routeName}
                  onItemClick={this.closeFlyouts}
                  helpSupport={helpSupport}
                />
              </FlyoutContainer>
            </MenuFlyout>
          </CSSTransition>

          {/* User Menu Flyout */}
          <CSSTransition
            in={menusOpen.indexOf('userMenu') > -1}
            timeout={400}
            classNames="flyoutRight"
            onEntering={() => {
              forceScrollCB(true);
            }}
            onEntered={() => {
              this.toggleBodyLock(true);
            }}
            onExited={() => {
              forceScrollCB(false);
            }}
          >
            <UserFlyout>
              <FlyoutBar className="user">
                <SpacerContainer />
                <CloseBack
                  width="20px"
                  height="20px"
                  className="close"
                  href="#"
                  onClick={(e) => {
                    this.closeMenu(e);
                  }}
                >
                  <SVGIcon
                    selectedIcon="close"
                    width="20px"
                    height="20px"
                    iconColor={theme ? theme.lightText : 'white'}
                  />
                </CloseBack>
              </FlyoutBar>
              <FlyoutContainer className="user">
                <UserPreview>
                  <UserPic>
                    <UserAvatar src={userProfilePicture} initials={initials} alt={`${userFirstName} ${userLastName}`} />
                  </UserPic>
                  <div className="name">
                    {userFirstName} {userLastName}
                  </div>
                  <div className="role">{userJobTitle}</div>
                  {type === 'supplier' && (
                    <PreviewNumbers>
                      <LocaleLink route="Dashboard" passHref>
                        <PreviewNumber>
                          <div className="number">{numRequests}</div>
                          <div>Requests</div>
                        </PreviewNumber>
                      </LocaleLink>
                      <LocaleLink route="Dashboard" passHref>
                        <PreviewNumber>
                          <div className="number">{numBookings}</div>
                          <div>Bookings</div>
                        </PreviewNumber>
                      </LocaleLink>
                      <LocaleLink route="Dashboard" passHref>
                        <PreviewNumber>
                          <div className="number">{numProperties}</div>
                          <div>Properties</div>
                        </PreviewNumber>
                      </LocaleLink>
                    </PreviewNumbers>
                  )}
                </UserPreview>
                <LinkList links={MobileNav.createNavLinks(userMenuLinks, this.closeFlyouts)} type="stacked" />
                {type === 'supplier' && (
                  <OrgSelect
                    organizations={organizations}
                    userCurrentOrganization={userCurrentOrganization}
                    organizationChange={organizationChange}
                  />
                )}
                {MobileNav.createNavLinks(joinOrganization, this.closeFlyouts)}
                <StyledSignOut>{MobileNav.createNavLinks(signOut, this.closeFlyouts)}</StyledSignOut>
              </FlyoutContainer>
            </UserFlyout>
          </CSSTransition>
        </MobileWrapper>
        <CSSTransition in={menuVisible} timeout={400} classNames="underlay" unmountOnExit>
          <Underlay />
        </CSSTransition>
      </>
    );
  }
}

MobileNav.propTypes = {
  /**
   * Passed Component of the link that takes a user to the homepage.
   */
  logo: linkPropType,

  type: PropTypes.oneOf(['supplier', 'admin']).isRequired,

  isAdminToken: PropTypes.bool.isRequired,

  /**
   * Passed Component of the link that takes a user to the support page.
   */
  helpSupport: linkPropType,

  /**
   * Authenticated Users First Name.
   */

  userFirstName: PropTypes.string,

  /**
   * Authenticated Users Last Name.
   */

  userLastName: PropTypes.string,

  /**
   * Authenticated Users Profile Pic (optional), defaults to users initials from First/Last Name.
   */

  userProfilePicture: PropTypes.string,

  /**
   * string of the telephone number for contacting lodgelink. only for mobile.
   */
  contactNumber: PropTypes.string,

  /**
   * An array of link objects passed through.
   */
  subMenuLinks: PropTypes.arrayOf(subLinkPropType),

  /**
   * nav links in the user menu
   */
  userMenuLinks: PropTypes.arrayOf(linkPropType),

  /**
   * List of Organizations the Authenticated user is associated to that when clicked sets current active organization
   */
  organizations: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      permissions: PropTypes.arrayOf(PropTypes.string),
      role: PropTypes.string,
      id: PropTypes.number.isRequired
    })
  ),

  /**
   * Link to sign out the current authenticated user.
   */
  signOut: linkPropType,

  /**
   * Change handler to send back selected org so all places can be updated by parent/ancestor
   */
  organizationChange: PropTypes.func,

  /**
   * Authenticated Users Currently active organization ID.
   */
  userCurrentOrganization: PropTypes.number,

  /**
   * Passed component that
   */
  joinOrganization: linkPropType,

  /**
   * name of current route
   * */
  routeName: PropTypes.string,

  /**
   *  callback to enable/disable scrolling down or up to trigger nav show/hide
   */
  forceScrollCB: PropTypes.func,

  theme: themePropType
};

MobileNav.defaultProps = {
  userMenuLinks: [],
  forceScrollCB: () => {},
  userFirstName: '',
  userLastName: '',
  userCurrentOrganization: undefined,
  userProfilePicture: null,
  subMenuLinks: [],
  organizations: [],
  organizationChange: () => {},
  joinOrganization: null,
  contactNumber: null,
  logo: null,
  helpSupport: null,
  signOut: null,
  routeName: '',
  theme: {}
};

const ComposedMobileNav = compose(
  withStyles({
    badge: {
      marginTop: '8px',
      marginRight: '3px',
      backgroundColor: colors.colorBlue
    }
  }),
  withTheme
)(MobileNav);
export default ComposedMobileNav;
